import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Building2,
  Tag,
  Copy
} from 'lucide-react';
import clientService from '../services/clientService';
import ClientSelector from './ClientSelector';

/**
 * ClientAttributeOverride Component
 * Manages client-specific product attributes, pricing, and part numbers
 */
const ClientAttributeOverride = ({
  productId,
  productInfo,
  onOverrideUpdate,
  readOnly = false
}) => {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [editingClient, setEditingClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  // Edit form state
  const [editForm, setEditForm] = useState({
    attribute_overrides: {
      name: '',
      client_part_number: '',
      specification: '',
      material: '',
      coating: ''
    },
    pricing_overrides: {
      unit_price: '',
      min_order_qty: '',
      lead_time: ''
    }
  });

  const loadOverrides = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setError('');

    try {
      // For demonstration, we'll simulate loading overrides
      // In real implementation, this would be multiple API calls for each client
      const mockOverrides = {
        'CLIENT_001': {
          client_id: 'CLIENT_001',
          attribute_overrides: {
            name: '定制扩铰刀',
            client_part_number: 'PART-A-001',
            specification: '客户定制规格 D=12mm L=100mm',
            material: 'HSS-M42',
            coating: 'TiAlN'
          },
          pricing_overrides: {
            unit_price: 45.80,
            min_order_qty: 50,
            lead_time: '3-5工作日'
          }
        }
      };

      setOverrides(mockOverrides);
    } catch (err) {
      console.error('Failed to load client overrides:', err);
      setError('加载客户配置失败');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const resetEditForm = useCallback(() => {
    setEditForm({
      attribute_overrides: {
        name: '',
        client_part_number: '',
        specification: '',
        material: '',
        coating: ''
      },
      pricing_overrides: {
        unit_price: '',
        min_order_qty: '',
        lead_time: ''
      }
    });
  }, []);

  useEffect(() => {
    if (productId) {
      loadOverrides();
    }
  }, [productId, loadOverrides]);

  const handleAddOverride = useCallback(async () => {
    if (!selectedClientId) return;

    setSaving(true);
    setError('');

    try {
      const overrideData = {
        attribute_overrides: editForm.attribute_overrides,
        pricing_overrides: editForm.pricing_overrides
      };

      await clientService.updateClientProductOverrides(selectedClientId, productId, overrideData);

      // Update local state
      setOverrides(prev => ({
        ...prev,
        [selectedClientId]: {
          client_id: selectedClientId,
          ...overrideData
        }
      }));

      setAddDialogOpen(false);
      setSelectedClientId('');
      resetEditForm();
      setSuccess('客户配置添加成功');

      onOverrideUpdate?.();
    } catch (err) {
      console.error('Failed to add client override:', err);
      setError(err.response?.data?.error || '添加客户配置失败');
    } finally {
      setSaving(false);
    }
  }, [selectedClientId, productId, editForm, onOverrideUpdate, resetEditForm]);

  const handleUpdateOverride = useCallback(async (clientId) => {
    setSaving(true);
    setError('');

    try {
      const overrideData = {
        attribute_overrides: editForm.attribute_overrides,
        pricing_overrides: editForm.pricing_overrides
      };

      await clientService.updateClientProductOverrides(clientId, productId, overrideData);

      // Update local state
      setOverrides(prev => ({
        ...prev,
        [clientId]: {
          client_id: clientId,
          ...overrideData
        }
      }));

      setEditingClient(null);
      resetEditForm();
      setSuccess('客户配置更新成功');

      onOverrideUpdate?.();
    } catch (err) {
      console.error('Failed to update client override:', err);
      setError(err.response?.data?.error || '更新客户配置失败');
    } finally {
      setSaving(false);
    }
  }, [productId, editForm, onOverrideUpdate, resetEditForm]);

  const handleDeleteOverride = useCallback(async (clientId) => {
    setSaving(true);
    setError('');

    try {
      await clientService.deleteClientProductOverrides(clientId, productId);

      // Remove from local state
      setOverrides(prev => {
        const newOverrides = { ...prev };
        delete newOverrides[clientId];
        return newOverrides;
      });

      setDeleteDialogOpen(false);
      setClientToDelete(null);
      setSuccess('客户配置已删除');

      onOverrideUpdate?.();
    } catch (err) {
      console.error('Failed to delete client override:', err);
      setError(err.response?.data?.error || '删除客户配置失败');
    } finally {
      setSaving(false);
    }
  }, [productId, onOverrideUpdate]);

  const handleEditStart = useCallback((clientId, override) => {
    setEditingClient(clientId);
    setEditForm({
      attribute_overrides: {
        name: override.attribute_overrides?.name || '',
        client_part_number: override.attribute_overrides?.client_part_number || '',
        specification: override.attribute_overrides?.specification || '',
        material: override.attribute_overrides?.material || '',
        coating: override.attribute_overrides?.coating || ''
      },
      pricing_overrides: {
        unit_price: override.pricing_overrides?.unit_price || '',
        min_order_qty: override.pricing_overrides?.min_order_qty || '',
        lead_time: override.pricing_overrides?.lead_time || ''
      }
    });
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingClient(null);
    resetEditForm();
  }, [resetEditForm]);

  const handleFormChange = useCallback((category, field, value) => {
    setEditForm(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  }, []);

  const handleCopyFromOriginal = useCallback((category) => {
    if (!productInfo) return;

    let updates = {};

    if (category === 'attribute_overrides') {
      updates = {
        name: productInfo.name || '',
        client_part_number: productInfo.part_number || '',
        specification: productInfo.specification || '',
        material: productInfo.material || '',
        coating: productInfo.coating || ''
      };
    } else if (category === 'pricing_overrides') {
      updates = {
        unit_price: productInfo.price || '',
        min_order_qty: productInfo.min_order_qty || '',
        lead_time: productInfo.lead_time || ''
      };
    }

    setEditForm(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        ...updates
      }
    }));
  }, [productInfo]);

  const toggleExpanded = (clientId) => {
    setExpandedItems(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  if (!productId) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">请先保存产品以管理客户配置</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">客户专属配置</h3>
        </div>

        {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            disabled={loading || saving}
          >
            <Plus className="h-4 w-4 mr-2" />
            添加客户配置
          </Button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <Button variant="ghost" size="sm" onClick={() => setError('')}>×</Button>
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 border-success/20 bg-success/10">
          <AlertDescription className="flex items-center justify-between text-success">
            {success}
            <Button variant="ghost" size="sm" onClick={() => setSuccess('')}>×</Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Client Overrides List */}
      {Object.keys(overrides).length > 0 ? (
        <div className="space-y-3">
          {Object.entries(overrides).map(([clientId, override]) => {
            const isEditing = editingClient === clientId;
            const isExpanded = expandedItems[clientId] ?? true;

            return (
              <Collapsible
                key={clientId}
                open={isExpanded}
                onOpenChange={() => toggleExpanded(clientId)}
              >
                <Card>
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted">
                      <div className="flex items-center gap-3">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                        <span className="font-medium">客户 {clientId}</span>

                        {override.attribute_overrides?.name && (
                          <Badge variant="outline" className="text-primary border-primary/30">
                            定制: {override.attribute_overrides.name}
                          </Badge>
                        )}

                        {override.pricing_overrides?.unit_price && (
                          <Badge variant="outline" className="text-success border-green-300">
                            ¥{override.pricing_overrides.unit_price}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {!readOnly && (
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      isEditing
                                        ? handleUpdateOverride(clientId)
                                        : handleEditStart(clientId, override)
                                    }
                                    disabled={saving}
                                    className={isEditing ? 'text-primary' : ''}
                                  >
                                    {isEditing ? <Save className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>{isEditing ? '保存' : '编辑'}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            {isEditing ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" onClick={handleEditCancel}>
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>取消</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-destructive"
                                      onClick={() => {
                                        setClientToDelete(clientId);
                                        setDeleteDialogOpen(true);
                                      }}
                                      disabled={saving}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>删除</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        )}
                        <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Attribute Overrides */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-primary">属性配置</h4>
                            {isEditing && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleCopyFromOriginal('attribute_overrides')}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>复制原始值</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>

                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs">客户产品名称</Label>
                              <Input
                                value={isEditing ? editForm.attribute_overrides.name : (override.attribute_overrides?.name || '')}
                                onChange={(e) => handleFormChange('attribute_overrides', 'name', e.target.value)}
                                disabled={!isEditing}
                                placeholder={productInfo?.name || ''}
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <Label className="text-xs">客户零件号</Label>
                              <Input
                                value={isEditing ? editForm.attribute_overrides.client_part_number : (override.attribute_overrides?.client_part_number || '')}
                                onChange={(e) => handleFormChange('attribute_overrides', 'client_part_number', e.target.value)}
                                disabled={!isEditing}
                                placeholder={productInfo?.part_number || ''}
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <Label className="text-xs">客户规格要求</Label>
                              <Textarea
                                value={isEditing ? editForm.attribute_overrides.specification : (override.attribute_overrides?.specification || '')}
                                onChange={(e) => handleFormChange('attribute_overrides', 'specification', e.target.value)}
                                disabled={!isEditing}
                                placeholder={productInfo?.specification || ''}
                                rows={2}
                                className="mt-1"
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">材料</Label>
                                <Input
                                  value={isEditing ? editForm.attribute_overrides.material : (override.attribute_overrides?.material || '')}
                                  onChange={(e) => handleFormChange('attribute_overrides', 'material', e.target.value)}
                                  disabled={!isEditing}
                                  placeholder={productInfo?.material || ''}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">涂层</Label>
                                <Input
                                  value={isEditing ? editForm.attribute_overrides.coating : (override.attribute_overrides?.coating || '')}
                                  onChange={(e) => handleFormChange('attribute_overrides', 'coating', e.target.value)}
                                  disabled={!isEditing}
                                  placeholder={productInfo?.coating || ''}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Pricing Overrides */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-success">定价配置</h4>
                            {isEditing && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleCopyFromOriginal('pricing_overrides')}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>复制原始值</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>

                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs">客户单价</Label>
                              <div className="relative mt-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">¥</span>
                                <Input
                                  type="number"
                                  value={isEditing ? editForm.pricing_overrides.unit_price : (override.pricing_overrides?.unit_price || '')}
                                  onChange={(e) => handleFormChange('pricing_overrides', 'unit_price', parseFloat(e.target.value) || '')}
                                  disabled={!isEditing}
                                  placeholder={productInfo?.price?.toString() || ''}
                                  className="pl-8"
                                />
                              </div>
                            </div>

                            <div>
                              <Label className="text-xs">最小起订量</Label>
                              <Input
                                type="number"
                                value={isEditing ? editForm.pricing_overrides.min_order_qty : (override.pricing_overrides?.min_order_qty || '')}
                                onChange={(e) => handleFormChange('pricing_overrides', 'min_order_qty', parseInt(e.target.value) || '')}
                                disabled={!isEditing}
                                placeholder={productInfo?.min_order_qty?.toString() || ''}
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <Label className="text-xs">交货周期</Label>
                              <Input
                                value={isEditing ? editForm.pricing_overrides.lead_time : (override.pricing_overrides?.lead_time || '')}
                                onChange={(e) => handleFormChange('pricing_overrides', 'lead_time', e.target.value)}
                                disabled={!isEditing}
                                placeholder={productInfo?.lead_time || ''}
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground mb-1">暂无客户专属配置</p>
            <p className="text-sm text-muted-foreground">添加客户配置以为特定客户定制产品属性和定价</p>
          </CardContent>
        </Card>
      )}

      {/* Add Client Override Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>添加客户专属配置</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div>
              <ClientSelector
                selectedClientId={selectedClientId}
                onClientChange={(clientId) => setSelectedClientId(clientId)}
                showGlobalOption={false}
                label="选择客户"
                helperText="选择要配置专属属性的客户"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Attribute Overrides */}
              <div>
                <h4 className="text-sm font-medium text-primary mb-3">属性配置</h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">客户产品名称</Label>
                    <Input
                      value={editForm.attribute_overrides.name}
                      onChange={(e) => handleFormChange('attribute_overrides', 'name', e.target.value)}
                      placeholder={productInfo?.name || ''}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing Overrides */}
              <div>
                <h4 className="text-sm font-medium text-success mb-3">定价配置</h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">客户单价</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">¥</span>
                      <Input
                        type="number"
                        value={editForm.pricing_overrides.unit_price}
                        onChange={(e) => handleFormChange('pricing_overrides', 'unit_price', parseFloat(e.target.value) || '')}
                        placeholder={productInfo?.price?.toString() || ''}
                        className="pl-8"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>取消</Button>
            <Button
              onClick={handleAddOverride}
              disabled={!selectedClientId || saving}
            >
              添加配置
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
          </DialogHeader>
          <p className="py-4">
            确定要删除客户 {clientToDelete} 的专属配置吗？此操作不可撤销。
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>取消</Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteOverride(clientToDelete)}
              disabled={saving}
            >
              删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientAttributeOverride;
